import { Link } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import type { ReactNode } from 'react';
import { lastSearch } from '@/lib/last-search';
import type { FileRoutesByTo } from '@/routeTree.gen';

/**
 * The "← Back to …" link at the top of a detail or edit page.
 *
 * Returns to the page as the user left it — search, filters, sort, page, tab —
 * via `lastSearch`, so a new page that uses this gets that for free rather than
 * having to remember to pass `search` (the bug this replaced: every hand-rolled
 * back link opened the bare, unfiltered list).
 */
export function BackLink({
  to,
  params,
  children,
}: {
  to: keyof FileRoutesByTo;
  params?: Record<string, string>;
  children: ReactNode;
}) {
  return (
    <Link
      // `to` is any route, so the per-route `params`/`search` types can't be
      // resolved here; `lastSearch` already yields what that route accepted.
      to={to as never}
      params={params as never}
      search={lastSearch(to, { params }) as never}
      className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
    >
      <ArrowLeft className="size-3" /> {children}
    </Link>
  );
}
